import type { Attachment } from "@/api/attachment";
import {
  editRecipeSchema,
  type EditRecipe,
} from "@/components/images/image-studio/edit-recipe.schema";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { resolveEmbedThumbnailUrl } from "@/lib/media/embed";

/** Positions techniques (slot) — ne doivent jamais servir de libellé d'affichage. */
const TECHNICAL_POSITIONS = new Set(["cmsPuckMedia"]);

/**
 * Filtre médiathèque CMS avancé : médias uploadés via Puck (`docId = CMS.id`)
 * ET médias de l'ancien CMS (covers / slots page → `docId = Instance.id`).
 * KEEP IN SYNC MediaPickerField / GalleryPickerField / medias.tsx.
 */
export function cmsMediaLibraryWhere(
  cmsId: string | undefined,
  instanceId: string | undefined,
): { docId: string } | { OR: { docId: string }[] } {
  if (cmsId && instanceId && cmsId !== instanceId) {
    return { OR: [{ docId: cmsId }, { docId: instanceId }] };
  }
  if (cmsId) return { docId: cmsId };
  if (instanceId) return { docId: instanceId };
  return { docId: "__none__" };
}

function firstCaption(att: Attachment): string {
  const caption = (att.caption ?? {}) as Record<string, string>;
  return caption.fr || Object.values(caption).find(Boolean) || "";
}

/** Libellé d'affichage d'un attachment (légende FR, nom de fichier, ou id tronqué). */
export function attachmentTitle(
  att: Attachment | undefined | null,
  id = "",
): string {
  if (!att) return id ? `Média ${id.slice(0, 6)}` : "Média";
  const caption = firstCaption(att);
  if (caption) return caption;
  if (att.name?.trim()) return att.name.trim();
  // `position` est un slot technique (ex. cmsPuckMedia) — évité en libellé.
  if (att.position && !TECHNICAL_POSITIONS.has(att.position)) {
    return att.position;
  }
  // Dernier recours utile : basename S3 (`link`) plutôt que « Média <id> ».
  const link = (att as { link?: string }).link?.trim();
  if (link) {
    const base = link.split("/").pop() || link;
    // Retire suffixes de format (-large.webp, -moyen.webp, etc.)
    return base.replace(/-(?:petit|moyen|large|original)(?:\.[a-z0-9]+)?$/i, "");
  }
  return `Média ${att.id.slice(0, 6)}`;
}

/** Dimensions px stockées dans `details` à l'upload (sinon null). */
export function attachmentStoredPixelSize(
  att: Attachment | undefined | null,
): { width: number; height: number } | null {
  if (!att?.details) return null;
  try {
    const raw =
      typeof att.details === "string" ? JSON.parse(att.details) : att.details;
    if (!raw || typeof raw !== "object") return null;
    const width = Number((raw as { width?: unknown }).width);
    const height = Number((raw as { height?: unknown }).height);
    if (
      Number.isFinite(width) &&
      Number.isFinite(height) &&
      width > 0 &&
      height > 0
    ) {
      return { width: Math.round(width), height: Math.round(height) };
    }
  } catch {
    // ignore JSON invalide
  }
  return null;
}

/** Parse `attachment.editRecipe` (objet ou string JSON) en recette typée, ou null. */
export function parseEditRecipe(raw: unknown): EditRecipe | null {
  if (raw == null || raw === "") return null;
  let value: unknown = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const parsed = editRecipeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** La retouche modifie-t-elle les DIMENSIONS de sortie (crop / rotation / perspective) ? */
export function editRecipeChangesSize(recipe: EditRecipe | null): boolean {
  if (!recipe) return false;
  return (
    !!recipe.crop || recipe.rotate % 360 !== 0 || !!recipe.perspective
  );
}

/**
 * Dimensions EFFECTIVES de l'image après retouche, à partir des dimensions SOURCE
 * (`details`). Reproduit l'ordre du convertisseur Sharp : rotation PUIS crop (le
 * crop est en fractions de l'image déjà tournée). Une rotation multiple de 90°
 * échange largeur/hauteur ; un angle quelconque élargit la boîte englobante. La
 * perspective n'est pas modélisée finement (rare) → dimensions inchangées pour ce
 * seul cas. Sans retouche de taille, renvoie la source telle quelle.
 * KEEP IN SYNC : ordre d'application de `edit-recipe.schema.ts`
 * (perspective → rotate → crop).
 */
export function editedPixelSize(
  source: { width: number; height: number },
  recipe: EditRecipe | null,
): { width: number; height: number } {
  if (!editRecipeChangesSize(recipe) || !recipe) return source;
  let width = source.width;
  let height = source.height;

  // Rotation (avant le crop). Multiples de 90° → échange ; sinon boîte englobante.
  const deg = ((recipe.rotate % 360) + 360) % 360;
  if (deg === 90 || deg === 270) {
    [width, height] = [height, width];
  } else if (deg !== 0 && deg !== 180) {
    const rad = (deg * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    const w = Math.round(width * cos + height * sin);
    const h = Math.round(width * sin + height * cos);
    width = Math.max(1, w);
    height = Math.max(1, h);
  }

  // Crop : fractions de l'image (déjà tournée).
  if (recipe.crop) {
    width = Math.max(1, Math.round(width * recipe.crop.w));
    height = Math.max(1, Math.round(height * recipe.crop.h));
  }

  return { width, height };
}

/** Libellé `1234 × 567 px` depuis `details`, ou null. */
export function attachmentPixelLabel(
  att: Attachment | undefined | null,
): string | null {
  const size = attachmentStoredPixelSize(att);
  if (!size) return null;
  return `${size.width} × ${size.height} px`;
}

/**
 * Dimensions max générées par le convertisseur (`fit: "inside"`).
 * KEEP IN SYNC inventozen-converter/src/controllers/convertisseur.js
 */
export const CONVERTER_IMAGE_MAX_PX = {
  petit: 100,
  medium: 600,
  large: 1600,
} as const;

/**
 * Même logique Sharp que le convertisseur : `fit: "inside"` +
 * `withoutEnlargement` → l'image entre dans le carré max en gardant son ratio.
 */
export function converterFitInsideDims(
  maxBoxPx: number,
  naturalWidth: number,
  naturalHeight: number,
): { width: number; height: number } {
  if (naturalWidth <= 0 || naturalHeight <= 0) {
    return { width: maxBoxPx, height: maxBoxPx };
  }
  if (naturalWidth <= maxBoxPx && naturalHeight <= maxBoxPx) {
    return {
      width: Math.round(naturalWidth),
      height: Math.round(naturalHeight),
    };
  }
  const scale = Math.min(maxBoxPx / naturalWidth, maxBoxPx / naturalHeight);
  return {
    width: Math.max(1, Math.round(naturalWidth * scale)),
    height: Math.max(1, Math.round(naturalHeight * scale)),
  };
}

function formatSizeDimsLabel(
  maxBoxPx: number,
  natural?: { width: number; height: number } | null,
): string {
  if (natural && natural.width > 0 && natural.height > 0) {
    const { width, height } = converterFitInsideDims(
      maxBoxPx,
      natural.width,
      natural.height,
    );
    return `(${width}x${height}px)`;
  }
  // Pas d'image / dimensions inconnues : cadre max du convertisseur.
  return `(${maxBoxPx}x${maxBoxPx}px)`;
}

/** Options du select « Taille (image) » Puck — libellé + dims convertisseur. */
export function puckMediaSizeFieldOptions(
  t: (key: `cms.new.${string}`, label: string) => string,
  natural?: { naturalWidth?: number; naturalHeight?: number } | null,
): Array<{ label: string; value: string }> {
  const size =
    natural?.naturalWidth && natural?.naturalHeight
      ? { width: natural.naturalWidth, height: natural.naturalHeight }
      : null;
  return [
    {
      label: `${t("cms.new.auto.label.petit", "Petit")} ${formatSizeDimsLabel(CONVERTER_IMAGE_MAX_PX.petit, size)}`,
      value: "petit",
    },
    {
      label: `${t("cms.new.auto.label.moyen", "Moyen")} ${formatSizeDimsLabel(CONVERTER_IMAGE_MAX_PX.medium, size)}`,
      value: "medium",
    },
    {
      label: `${t("cms.new.auto.label.grand", "Grand")} ${formatSizeDimsLabel(CONVERTER_IMAGE_MAX_PX.large, size)}`,
      value: "large",
    },
    {
      label: size
        ? `${t("cms.new.auto.label.original", "Original")} (${Math.round(size.width)}x${Math.round(size.height)}px)`
        : t("cms.new.auto.label.original", "Original"),
      value: "original",
    },
  ];
}

/** Vignette standard (medium → small → large). */
export function attachmentThumb(
  att: Attachment | undefined | null,
): string | undefined {
  if (!att) return undefined;
  if (att.kind === "video") {
    return (
      att.posterPublicUrl ?? att.mediumPublicUrl ?? att.publicUrl ?? undefined
    );
  }
  if (att.kind === "embed") {
    return (
      att.posterPublicUrl ??
      att.mediumPublicUrl ??
      att.publicUrl ??
      resolveEmbedThumbnailUrl(
        att.embedProvider,
        att.embedVideoId,
        att.embedUrl,
      ) ??
      undefined
    );
  }
  return att.mediumPublicUrl ?? att.publicUrl ?? att.largePublicUrl;
}

/** Meilleure URL pour mesurer les dimensions naturelles (original/large). */
export function attachmentMeasureUrl(
  att: Attachment | undefined | null,
): string | undefined {
  if (!att) return undefined;
  if (att.kind === "video" || att.kind === "embed") return undefined;
  const a = att as Attachment & { originalPublicUrl?: string };
  return (
    a.originalPublicUrl ||
    att.largePublicUrl ||
    att.mediumPublicUrl ||
    att.publicUrl
  );
}

/** Vignette grande (large → medium → small) pour le picker catalogue BO. */
export function attachmentLargeThumb(
  att: Attachment | undefined | null,
): string | undefined {
  if (!att) return undefined;
  if (att.kind === "video" || att.kind === "embed") {
    return (
      att.posterPublicUrl ??
      att.largePublicUrl ??
      att.mediumPublicUrl ??
      att.publicUrl
    );
  }
  return att.largePublicUrl ?? att.mediumPublicUrl ?? att.publicUrl;
}

export type CatalogMediaKind = "image" | "video" | "embed";

const DEFAULT_CATALOG_MEDIA_KINDS: CatalogMediaKind[] = [
  "image",
  "video",
  "embed",
];

/** Badge court pour les vignettes du picker catalogue (vide pour les images). */
export function catalogMediaKindBadge(kind?: string | null): string {
  if (kind === "video") return cmsNew("cms.new.medias.kind.video", "Vidéo");
  if (kind === "embed") return cmsNew("cms.new.medias.kind.embed", "Embed");
  return "";
}

/** Filtre les attachments catalogue selon les kinds autorisés (exclut génériques). */
export function filterCatalogMedia(
  attachments: Attachment[],
  allowedKinds: CatalogMediaKind[] = DEFAULT_CATALOG_MEDIA_KINDS,
): Attachment[] {
  const allowed = new Set(allowedKinds);
  return attachments.filter((a) => {
    if (a.isGenericImage) return false;
    const kind = (a.kind ?? "image") as CatalogMediaKind;
    return allowed.has(kind);
  });
}

/** Libellé sous une vignette d'image catalogue BO (légende, nom, puis position). */
export function catalogImageLabel(att: Attachment): string {
  const caption = firstCaption(att);
  if (caption) return caption;
  if (att.name?.trim()) return att.name.trim();
  if (att.position && !TECHNICAL_POSITIONS.has(att.position)) {
    return att.position;
  }
  return att.id.slice(0, 8);
}

/** Filtre les attachments image exploitables (exclut génériques, vidéos, embeds). */
export function filterCatalogImages(attachments: Attachment[]): Attachment[] {
  return filterCatalogMedia(attachments, ["image"]);
}
